import { useState } from 'react';
import { User, Phone, Mail, Calendar, MapPin, FileText, MessageCircle, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { buildInquirySummary, buildWhatsAppUrl } from '../lib/whatsapp';
import { useSettings } from '../context/SettingsContext';

const fields = [
  { name: 'name', label: 'Full Name', icon: User, type: 'text', required: true },
  { name: 'mobile', label: 'Mobile Number', icon: Phone, type: 'tel', required: true },
  { name: 'email', label: 'Email Address', icon: Mail, type: 'email' },
  { name: 'eventDate', label: 'Event Date', icon: Calendar, type: 'date' },
  { name: 'location', label: 'Event Location', icon: MapPin, type: 'text' },
];

export default function InquiryForm({ pkg = null, addOns = [], origin = 'contact', compact = false }) {
  const { showPricing } = useSettings();
  const [lead, setLead] = useState({
    name: '', mobile: '', email: '', eventDate: '', location: '', notes: '',
  });
  const [status, setStatus] = useState('idle'); // idle | sending | done | error

  const update = (k) => (e) => setLead((l) => ({ ...l, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!lead.name || !lead.mobile) return;
    setStatus('sending');

    // Always persist the real total so admin sees it, regardless of display setting.
    const payload = {
      ...lead,
      origin,
      source: 'website',
      package: pkg ? { id: pkg.id, title: pkg.title, price: pkg.price } : null,
      addOns: addOns.map((a) => ({ id: a.id, title: a.title, price: a.price })),
      total: (pkg?.price || 0) + addOns.reduce((s, a) => s + (a.price || 0), 0),
    };

    // Persist the inquiry (best-effort; WhatsApp still opens if API is offline).
    try {
      await api.post('/inquiries', payload);
    } catch (err) {
      console.warn('Inquiry save failed (continuing to WhatsApp):', err?.message);
    }

    const summary = buildInquirySummary({ pkg, addOns, lead, showPricing });
    window.open(buildWhatsAppUrl(summary), '_blank');
    setStatus('done');
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className={`grid gap-4 ${compact ? '' : 'sm:grid-cols-2'}`}>
        {fields.map((f) => (
          <label key={f.name} className="block">
            <span className="mb-1.5 flex items-center gap-1.5 text-sm text-ink-300">
              <f.icon size={14} /> {f.label} {f.required && <span className="text-danger">*</span>}
            </span>
            <input
              type={f.type}
              required={f.required}
              value={lead[f.name]}
              onChange={update(f.name)}
              className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none transition focus:border-brand focus:bg-white/10"
            />
          </label>
        ))}
      </div>

      <label className="block">
        <span className="mb-1.5 flex items-center gap-1.5 text-sm text-ink-300">
          <FileText size={14} /> Additional Notes
        </span>
        <textarea
          rows={3}
          value={lead.notes}
          onChange={update('notes')}
          placeholder="Tell us about your vision, guest count, preferences…"
          className="w-full resize-none rounded-lg border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none transition focus:border-brand focus:bg-white/10 placeholder:text-ink-500"
        />
      </label>

      <button
        type="submit"
        disabled={status === 'sending'}
        className="btn-whatsapp w-full disabled:opacity-60"
      >
        {status === 'sending' ? (
          <><Loader2 size={18} className="animate-spin" /> Sending…</>
        ) : (
          <><MessageCircle size={18} /> Send Inquiry via WhatsApp</>
        )}
      </button>

      {status === 'done' && (
        <p className="text-center text-sm text-success">
          ✓ Inquiry recorded — continue the chat on WhatsApp.
        </p>
      )}
    </form>
  );
}
